import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { 
  Users, 
  Star, 
  Image as ImageIcon, 
  Bed, 
  CheckSquare, 
  Layers,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  ArrowRight,
  FileText,
  Plus,
  Settings
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, delay }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-white p-6 rounded-2xl shadow-sm border border-accent/10 flex items-center gap-6"
  >
    <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <div>
      <p className="text-xs font-bold text-[#59635C] uppercase tracking-widest font-clean">{title}</p>
      <h3 className="text-3xl font-serif text-emerald mt-1">{value}</h3>
    </div>
  </motion.div>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalReviews: 0,
    pendingReviews: 0,
    approvedReviews: 0,
    galleryPhotos: 0,
    rooms: 0,
    amenities: 0,
    sections: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentReviews, setRecentReviews] = useState<any[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          reviewsRes,
          galleryRes,
          roomsRes,
          amenitiesRes,
          sectionsRes
        ] = await Promise.all([
          supabase.from('reviews').select('status', { count: 'exact' }),
          supabase.from('gallery_items').select('id', { count: 'exact' }),
          supabase.from('rooms').select('id', { count: 'exact' }),
          supabase.from('amenities').select('id', { count: 'exact' }),
          supabase.from('website_sections').select('id', { count: 'exact' })
        ]);

        const reviews = reviewsRes.data || [];
        
        setStats({
          totalReviews: reviews.length,
          pendingReviews: reviews.filter(r => r.status === 'pending').length,
          approvedReviews: reviews.filter(r => r.status === 'approved').length,
          galleryPhotos: galleryRes.count || 0,
          rooms: roomsRes.count || 0,
          amenities: amenitiesRes.count || 0,
          sections: sectionsRes.count || 0
        });

        const { data: recent } = await supabase
          .from('reviews')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);
        
        setRecentReviews(recent || []);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-serif text-emerald">Dashboard Overview</h1>
          <p className="text-[#59635C] font-clean mt-2">Welcome to your management command center.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Pending Reviews" 
            value={stats.pendingReviews} 
            icon={Clock} 
            color="bg-amber-500" 
            delay={0.1} 
          />
          <StatCard 
            title="Approved Reviews" 
            value={stats.approvedReviews} 
            icon={CheckCircle2} 
            color="bg-emerald" 
            delay={0.2} 
          />
          <StatCard 
            title="Gallery Photos" 
            value={stats.galleryPhotos} 
            icon={ImageIcon} 
            color="bg-blue-500" 
            delay={0.3} 
          />
          <StatCard 
            title="Total Rooms" 
            value={stats.rooms} 
            icon={Bed} 
            color="bg-purple-500" 
            delay={0.4} 
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            <h2 className="text-xl font-serif text-emerald">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-4">
              {[
                { label: 'Edit Hero Content', path: '/admin/content', icon: FileText },
                { label: 'Add Gallery Photo', path: '/admin/gallery', icon: Plus },
                { label: 'Moderate Reviews', path: '/admin/reviews', icon: Star },
                { label: 'Site Settings', path: '/admin/settings', icon: Settings },
              ].map((action, i) => (
                <Link
                  key={i}
                  to={action.path}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-accent/10 hover:border-emerald hover:shadow-md transition-all group"
                >
                  <span className="font-clean font-medium text-emerald">{action.label}</span>
                  <ArrowRight className="w-4 h-4 text-emerald opacity-0 group-hover:opacity-100 transition-all" />
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Reviews */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-serif text-emerald">Recent Reviews</h2>
              <Link to="/admin/reviews" className="text-emerald font-clean font-bold text-xs uppercase tracking-widest hover:underline">
                View All
              </Link>
            </div>
            
            <div className="bg-white rounded-2xl border border-accent/10 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left font-clean">
                  <thead className="bg-[#F5F1E8] text-[#59635C] text-xs font-bold uppercase tracking-widest">
                    <tr>
                      <th className="px-6 py-4">Guest</th>
                      <th className="px-6 py-4">Rating</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-accent/10">
                    {recentReviews.map((review) => (
                      <tr key={review.id} className="hover:bg-[#F5F1E8]/50 transition-colors">
                        <td className="px-6 py-4 font-bold text-emerald">{review.guest_name || review.name}</td>
                        <td className="px-6 py-4">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={12}
                                className={`${i < review.rating ? "text-accent fill-accent" : "text-gray-200"}`}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                            review.status === 'approved' ? 'bg-green-100 text-green-700' :
                            review.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {review.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-[#59635C]/60">
                          {new Date(review.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                    {recentReviews.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-[#59635C]/60 italic">No reviews yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
